import {
  IMDSIconButtonProps,
  MDSIconButtonSize,
  MDSIconButtonVariant,
} from "@/mds/components/buttons/icon-button/icon-button.types";
import { MDSTheme } from "@/mds/themes";
import { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { Button } from "react-aria-components";

export const StyledButton = styled(Button)<Omit<IMDSIconButtonProps, "iconKind">>(
  ({
    theme,
    variant = MDSIconButtonVariant.Default,
    size = MDSIconButtonSize.Medium,
    isToggled = false,
    rounded = false,
  }) => ({
    position: "relative",
    display: "flex",
    textWrap: "nowrap",
    flexDirection: "row",
    gap: theme.spacing["1"],
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "nowrap",

    cursor: "pointer",
    transition: "0.25s ease",
    userSelect: "none",

    borderRadius: getBorderRadius({ theme, variant, rounded }),
    padding: getPadding({ size }),

    /**
     * On desktop, the outer region is draggable.
     * We want to ensure this card is not draggable.
     */
    WebkitAppRegion: "no-drag",

    ...getColors({ variant, theme, isToggled }),
    ...getIconStyles({ theme, size, variant, isToggled }),
  })
);

const getPadding = ({ size }: { size: MDSIconButtonSize }) => {
  if (size === MDSIconButtonSize.XSmall) return "0px";
  if (size === MDSIconButtonSize.Small) return "8px";
  if (size === MDSIconButtonSize.Medium) return "12px";
};

const getBorderRadius = ({
  theme,
  rounded,
}: {
  theme: MDSTheme;
  variant: MDSIconButtonVariant;
  rounded: boolean;
}) => {
  // TODO: More variants
  if (rounded) return theme.borderRadius.max;
  else return theme.borderRadius.medium;
};

const getColors = ({
  theme,
  variant,
}: {
  theme: Theme;
  variant: MDSIconButtonVariant;
  isToggled: boolean;
}) => {
  const baselineStyle = {
    color: theme.colors.text.primary,
    backgroundColor: theme.colors.background.button.transparent.default,

    "&[data-hovered]:not([data-disabled])": {
      backgroundColor: theme.colors.background.hoverOverlay.default,
      transition: "0.2s all",
    },

    "&[data-disabled]": {
      cursor: "not-allowed",
      opacity: 0.4,
    },
  };

  switch (variant) {
    case MDSIconButtonVariant.Default:
      return baselineStyle;

    case MDSIconButtonVariant.Transparent:
      return {
        ...baselineStyle,
        backgroundColor: "transparent",
        borderColor: "transparent",

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.transparent.default,
          transition: "0.2s all",
        },
      };

    case MDSIconButtonVariant.TabBar:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.tabBar.default,
        color: theme.colors.text.onAccent,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.tabBar.hover,
          transition: "0.2s all",
        },
      };

    case MDSIconButtonVariant.OnAccent:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.onAccent.default,
        color: theme.colors.text.onAccent,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.hoverOverlay.default,
          transition: "0.2s all",
        },
      };

    case MDSIconButtonVariant.OnChrome:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.onChrome.default,
        color: theme.colors.icon.secondary,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.hoverOverlay.chrome,
          transition: "0.2s all",
        },
      };

    case MDSIconButtonVariant.Chrome:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.chrome.default,
        color: theme.colors.icon.secondary,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.hoverOverlay["chrome-2"],
          transition: "0.2s all",
        },
      };

    case MDSIconButtonVariant.Brand:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.brand.default,
        color: theme.colors.text.onAccent,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.brand.hover,
          transition: "0.2s all",
        },

        "&[data-disabled]": {
          backgroundColor: "transparent",
          cursor: "not-allowed",
        },
      };

    case MDSIconButtonVariant.OnEmphasis:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.transparent.default,
        color: theme.colors.icon.emphasis,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.hoverOverlay.emphasis,
        },

        "&[data-disabled]": {
          backgroundColor: theme.colors.background.button.transparent.default,
          cursor: "not-allowed",
        },
      };

    case MDSIconButtonVariant.Floating:
      return {
        ...baselineStyle,
        color: theme.colors.text.primary,
        backgroundColor: theme.colors.background.button.floatingIcon.default,
        boxShadow: theme.shadows.surface,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.floatingIcon.hover,
          transition: "0.2s all",
        },
      };

    default:
      return baselineStyle;
  }
};

const getIconStyles = ({
  theme,
  size,
  variant,
  isToggled,
}: {
  theme: Theme;
  size: MDSIconButtonSize;
  variant: MDSIconButtonVariant;
  isToggled: boolean;
}) => {
  const baselineStyle = {
    color: isToggled ? theme.colors.icon.selected : theme.colors.icon.primary,
    fill: isToggled ? theme.colors.icon.selected : theme.colors.icon.primary,
    fontSize: size === MDSIconButtonSize.XSmall ? "12px" : "14px",
    width: size === MDSIconButtonSize.XSmall ? "12px" : "14px",
    height: size === MDSIconButtonSize.XSmall ? "12px" : "14px",
  };

  switch (variant) {
    case MDSIconButtonVariant.OnChrome:
      return {
        ".mds-icon": {
          ...baselineStyle,
          color: theme.colors.icon.secondary,
          fill: theme.colors.icon.secondary,
        },
      };
    case MDSIconButtonVariant.OnEmphasis:
      return {
        ".mds-icon": {
          ...baselineStyle,
          color: theme.colors.icon.emphasis,
          fill: theme.colors.icon.emphasis,
        },
      };
    case MDSIconButtonVariant.Brand:
      return {
        ".mds-icon": {
          ...baselineStyle,
          color: theme.colors.icon.onAccent,
          fill: theme.colors.icon.onAccent,
        },
        "&[data-disabled] .mds-icon": {
          color: theme.colors.icon.placeholder,
          fill: theme.colors.icon.placeholder,
        },
      };
    default:
      return { ".mds-icon": baselineStyle };
  }
};

export const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  width: 16px;

  // Necessary for Chrome Ext
  svg {
    height: 14px;
    width: 14px;
  }
`;
